import { Link } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Button } from "./ui/button";
import CountdownTimer from "./CountdownTimer";
import { Users, ArrowRight, Truck } from "lucide-react";

const conditionLabels = {
  new: { label: "Nuevo", color: "bg-green-100 text-green-700 border-green-200" },
  used: { label: "Usado", color: "bg-amber-100 text-amber-700 border-amber-200" },
  cpo: { label: "CPO", color: "bg-blue-100 text-blue-700 border-blue-200" }
};

export default function DealCard({ package: pkg, index = 0 }) {
  const progress = (pkg.current_participants / pkg.min_participants) * 100;
  const savings = pkg.original_price - pkg.group_price;
  const savingsPercent = Math.round((savings / pkg.original_price) * 100);
  const condition = conditionLabels[pkg.product_condition] || conditionLabels.new;
  const deliveryDays = pkg.estimated_delivery_days || 7;

  return (
    <Link to={`/package/${pkg.package_id}`} data-testid={`deal-card-${pkg.package_id}`}>
      <Card 
        className="group relative bg-white rounded-2xl border border-border/50 overflow-hidden hover:border-orange-200 transition-all duration-300 card-shadow animate-fade-in"
        style={{ animationDelay: `${index * 100}ms` }}
      >
        {/* Product Image */}
        <div className="relative aspect-square overflow-hidden bg-gray-50">
          <img 
            src={pkg.product_image} 
            alt={pkg.product_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          <Badge className={`absolute top-3 left-3 ${condition.color}`}>
            {condition.label}
          </Badge>
          <Badge className="absolute top-3 right-3 bg-orange-500 text-white border-orange-500">
            -{savingsPercent}%
          </Badge>
        </div>

        <CardContent className="p-5">
          {/* Product Info */}
          <h3 className="font-semibold text-lg mb-2 line-clamp-1 group-hover:text-orange-600 transition-colors" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {pkg.product_name}
          </h3>

          {/* Pricing */}
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-2xl font-bold text-primary" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {pkg.group_price.toFixed(2)}€
            </span>
            <span className="text-sm text-muted-foreground line-through">
              {pkg.original_price.toFixed(2)}€
            </span>
          </div>

          {/* Delivery Time */}
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3">
            <Truck className="h-4 w-4" />
            <span>Entrega en {deliveryDays} {deliveryDays === 1 ? 'día' : 'días'}</span>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Users className="h-4 w-4" />
                <span>{pkg.current_participants}/{pkg.min_participants}</span>
              </div>
              <span className="text-xs text-muted-foreground">
                {pkg.min_participants - pkg.current_participants > 0 
                  ? `Faltan ${pkg.min_participants - pkg.current_participants}`
                  : "¡Mínimo alcanzado!"}
              </span>
            </div>
            <Progress value={Math.min(progress, 100)} className="h-2" />
          </div>

          {/* Countdown */}
          <div className="mb-4">
            <CountdownTimer endDate={pkg.ends_at} compact />
          </div>

          {/* CTA */}
          <Button className="w-full rounded-full bg-primary hover:bg-primary/90 group-hover:bg-orange-500 group-hover:hover:bg-orange-600 transition-colors">
            Ver oferta
            <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </CardContent>
      </Card>
    </Link>
  );
}
